import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useEffect, useState } from 'react';
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
  calculateBookingPriceURL,
  createBookingReservationURL,
  GET_CURRENCIES_URL,
  fetchFacilityPaymentDetails,
  UpdateBookingInvoicePaymentURL,
  getUsersURL
} from "../../../utils/urls";
import BookingCalendar from "./booking_calendar";
import reservationTrendService from "./reservation-trend-service";

// Picks the commission tier matching a stay's length (nights), preferring
// the narrowest/most specific matching tier. Falls back to the property's
// flat `commission` percentage when no tier matches or none are configured.
// Internal-only figure - never surfaced to the guest.
const resolveCommission = (bookingProperty, nights, totalAmount) => {
  const tiers = Array.isArray(bookingProperty?.commissionTiers) ? bookingProperty.commissionTiers : [];
  const matching = tiers.filter((tier) => {
    const min = Number(tier.minNights || 1);
    const max = tier.maxNights ? Number(tier.maxNights) : Infinity;
    return nights >= min && nights <= max;
  });

  if (matching.length > 0) {
    // Prefer the narrowest range (most specific tier) when several match.
    const tier = matching.reduce((best, current) => {
      const bestSpan = (best.maxNights || Infinity) - (best.minNights || 1);
      const currentSpan = (current.maxNights || Infinity) - (current.minNights || 1);
      return currentSpan < bestSpan ? current : best;
    });

    const amount = tier.type === 'fixed' ? Number(tier.value || 0) : (totalAmount * Number(tier.value || 0)) / 100;
    return { amount, rate: tier.type === 'fixed' ? null : Number(tier.value || 0) };
  }

  const rate = Number(bookingProperty?.commission || 10);
  return { amount: (totalAmount * rate) / 100, rate };
};

const BookingModal = ({
  visible,
  onHide,
  unit,
  onSuccess
}) => {
  // Check if apartment is managed by landlord
  const isManagedByLandlord = unit?.bookingProperty?.managedByLandlord || false;

  // State for currency (for property manager apartments only)
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // State for paybills (for Mobile Money payments)
  const [paybills, setPaybills] = useState([]);
  const [selectedPaybill, setSelectedPaybill] = useState(null);

  // Which staff member is making this booking - always an explicit manual
  // selection, never auto-filled from the logged-in user, so the invoice
  // can show "You were served by <name>".
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);

  // Enhanced guest information state
  const [guestInfo, setGuestInfo] = useState({
    name: "",
    email: "",
    phone: "",
    idNumber: ""
  });

  const [bookingForm, setBookingForm] = useState({
    checkIn: null,
    checkOut: null,
    guests: {
      adults: 1,
      children: 0
    },
    specialRequests: '',
    loading: false,
    bookingPrice: null,
    paymentMethod: 'Card',
    paymentTiming: 'Before',
    splitPaymentPercentage: 50
  });

  const [cashPayment, setCashPayment] = useState({
    amount: 0,
    receiptNumber: '',
    notes: ''
  });

  const isSameDay = (dateA, dateB) => {
    if (!dateA || !dateB) return false;
    return dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate();
  };

  const isCashAmountRequired = () => {
    if (bookingForm.paymentMethod !== 'Cash') {
      return false;
    }

    const checkInDate = bookingForm.checkIn ? new Date(bookingForm.checkIn) : null;
    const today = new Date();
    const isCheckInToday = checkInDate ? isSameDay(checkInDate, today) : false;
    return bookingForm.paymentTiming === 'Before' && isCheckInToday;
  };

  const normalizeReservationStatus = (status) => {
    const statusMap = {
      new: 'reserved',
      confirmed: 'booked',
      cancelled: 'canceled',
      canceled: 'canceled',
      reserved: 'reserved',
      booked: 'booked',
      completed: 'completed'
    };

    return statusMap[status] || status;
  };

  // Payment method options (only for property manager)
  const paymentMethods = [
    { name: 'Cash', value: 'Cash' },
    { name: 'Card', value: 'Card' },
    { name: 'Bank Transfer', value: 'Bank Transfer' },
    { name: 'Mobile Money', value: 'Mobile Money' },
    { name: 'Other', value: 'Other' }
  ];

  // Payment timing options (only for property manager)
  const paymentTimings = [
    { name: 'Before Check-in', value: 'Before' },
    { name: 'After Check-out', value: 'After' },
    { name: 'Split Payment', value: 'Split' },
    { name: 'Per Night', value: 'PerNight' }
  ];

  useEffect(() => {
    if (bookingForm.paymentMethod === 'Cash' && !cashPayment.receiptNumber) {
      const receiptNum = `RCT-${Date.now().toString().slice(-8)}`;
      setCashPayment((prev) => ({
        ...prev,
        receiptNumber: receiptNum
      }));
    }
  }, [bookingForm.paymentMethod, cashPayment.receiptNumber]);

  useEffect(() => {
    if (bookingForm.paymentMethod !== 'Cash') {
      return;
    }

    const totalAmount = bookingForm.bookingPrice?.totalAmount || 0;
    setCashPayment((prev) => ({
      ...prev,
      amount: prev.amount > 0 ? prev.amount : totalAmount
    }));
  }, [bookingForm.paymentMethod, bookingForm.bookingPrice?.totalAmount]);

  // Date normalization function
  const normalizeDate = (date) => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const normalized = new Date(year, month, day, 0, 0, 0, 0);

    console.log(`Date normalization:`, {
      original: date.toString(),
      originalMonth: date.getMonth(),
      year: year,
      month: month,
      day: day,
      normalized: normalized.toString(),
      normalizedMonth: normalized.getMonth()
    });

    return normalized;
  };

  // Fetch currency for property manager apartments only
  useEffect(() => {
    const fetchCurrency = async () => {
      if (isManagedByLandlord) {
        return; // Skip currency fetch for landlord managed apartments
      }

      try {
        const facilityId = await getItem("selectedFacilityId");
        const response = await makeRequest2(
          `${GET_CURRENCIES_URL}/${facilityId}`,
          "GET"
        );

        if (response.success) {
          const currencyData = response.data.data || response.data;

          if (!currencyData || !Array.isArray(currencyData) || currencyData.length === 0) {
            console.error("No currency data available");
            toastify("No currency data available", "error");
            return;
          }

          // Find the currency that matches the booking property's currency
          const propertyCurrencyId = unit?.bookingProperty?.currencyId;

          if (propertyCurrencyId) {
            const matchedCurrency = currencyData.find(c => c._id === propertyCurrencyId);

            if (matchedCurrency) {
              setSelectedCurrency(matchedCurrency);
              console.log("Found matching currency:", matchedCurrency);
            } else {
              console.warn("Property currency not found in available currencies, using first available");
              setSelectedCurrency(currencyData[0]);
            }
          } else {
            // Fallback to first currency
            setSelectedCurrency(currencyData[0]);
          }
        } else {
          console.error("Error fetching currencies:", response.error);
          toastify(response.error || "Failed to load currencies", "error");
        }
      } catch (error) {
        console.error("Error fetching currencies:", error);
        toastify("Failed to load currencies", "error");
      }
    };

    if (visible && unit?.bookingProperty) {
      fetchCurrency();
    }
  }, [visible, unit, isManagedByLandlord]);

  // Fetch facility paybills for Mobile Money payments
  useEffect(() => {
    const fetchPaybills = async () => {
      if (isManagedByLandlord) {
        setPaybills([]); // Reset paybills for landlord managed
        setSelectedPaybill(null);
        return;
      }

      try {
        const facilityId = await getItem("selectedFacilityId");
        const url = `${fetchFacilityPaymentDetails}/${facilityId}`;
        console.log("Fetching paybills from:", url);
        const response = await makeRequest2(url, "GET");
        console.log("Paybills response:", response);

        if (response.success && response.data?.length > 0) {
          console.log("Raw payment details:", response.data);

          // Format all payment details - show all available paybills
          // (module filtering should be done on backend, but for now show all)
          const formattedPaybills = response.data.map(item => ({
            _id: item._id,
            paybillNumber: item.shortCode,
            module: item.module || 'All',
            label: `${item.module || 'All'} - ${item.shortCode}`,
            hasCredentials: !!(item.passkey && item.authorizationKey)
          }));

          console.log("Formatted paybills:", formattedPaybills);
          setPaybills(Array.isArray(formattedPaybills) ? formattedPaybills : []);

          // Auto-select first paybill if available
          if (formattedPaybills.length > 0) {
            setSelectedPaybill(formattedPaybills[0]._id);
          } else {
            setSelectedPaybill(null);
          }
        } else {
          console.log("No payment details found or request failed");
          setPaybills([]);
          setSelectedPaybill(null);
        }
      } catch (error) {
        console.error("Error fetching paybills:", error);
        setPaybills([]);
        setSelectedPaybill(null);
      }
    };

    if (visible && unit?.bookingProperty) {
      fetchPaybills();
    } else if (!visible) {
      // Reset when modal closes
      setPaybills([]);
      setSelectedPaybill(null);
    }
  }, [visible, unit, isManagedByLandlord]);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const facilityId = await getItem("selectedFacilityId");
        const response = await makeRequest2(`${getUsersURL}/${facilityId}`, "GET");
        if (response.success) {
          const list = Array.isArray(response.data) ? response.data : (response.data?.data || []);
          setStaffList(list);
        } else {
          setStaffList([]);
        }
      } catch (error) {
        console.error("Error fetching staff list:", error);
        setStaffList([]);
      }
    };

    if (visible) {
      fetchStaff();
    } else {
      setStaffList([]);
      setSelectedStaffId(null);
    }
  }, [visible]);

  // Initialize booking price for property manager apartments
  useEffect(() => {
    if (unit && unit.bookingProperty && !isManagedByLandlord) {
      console.log("BookingModal received unit:", unit);

      const calculatedTotal = unit.bookingProperty.basePrice || 0;

      setBookingForm(prev => ({
        ...prev,
        bookingPrice: {
          basePrice: unit.bookingProperty.basePrice || 0,
          totalAmount: calculatedTotal,
          subTotal: calculatedTotal,
          nights: 1,
          currency: unit.bookingProperty.currencyId
        }
      }));
    }
  }, [unit, isManagedByLandlord]);

  // Calculate booking price when dates change (property manager only)
  useEffect(() => {
    if (isManagedByLandlord) {
      return; // Skip price calculation for landlord managed apartments
    }

    const calculatePrice = async () => {
      if (!bookingForm.checkIn || !bookingForm.checkOut) return;

      try {
        setBookingForm(prev => ({ ...prev, loading: true }));

        const facilityId = await getItem("selectedFacilityId");
        const bookingPropertyId = unit?.bookingProperty?.id || unit?.bookingProperty?._id;

        if (!bookingPropertyId) {
          console.error("No booking property ID available");
          return;
        }

        const normalizedCheckIn = normalizeDate(bookingForm.checkIn);
        const normalizedCheckOut = normalizeDate(bookingForm.checkOut);

        console.log("Calculating price with normalized dates:");
        console.log("Check-in:", normalizedCheckIn.toString(), "Month:", normalizedCheckIn.getMonth());
        console.log("Check-out:", normalizedCheckOut.toString(), "Month:", normalizedCheckOut.getMonth());

        const payload = {
          bookingPropertyId,
          checkIn: normalizedCheckIn.toISOString(),
          checkOut: normalizedCheckOut.toISOString()
        };

        console.log("Calculating price with payload:", payload);

        const response = await makeRequest2(
          `${calculateBookingPriceURL}/${facilityId}`,
          "POST",
          payload
        );

        console.log("Price calculation API response:", response);

        if (response.success) {
          const priceData = response.data;
          console.log("Price calculation data:", priceData);

          setBookingForm(prev => ({
            ...prev,
            bookingPrice: priceData
          }));
        } else {
          console.error("Error calculating price:", response.error);
          // Fallback calculation
          const nights = Math.ceil((normalizedCheckOut - normalizedCheckIn) / (1000 * 60 * 60 * 24));
          const basePrice = unit?.bookingProperty?.basePrice || 0;
          const calculatedTotal = basePrice * nights;

          setBookingForm(prev => ({
            ...prev,
            bookingPrice: {
              basePrice: basePrice,
              totalAmount: calculatedTotal,
              subTotal: calculatedTotal,
              nights: nights,
              currency: unit?.bookingProperty?.currencyId
            }
          }));
        }
      } catch (error) {
        console.error("Error calculating booking price:", error);
        // Fallback calculation on error
        if (bookingForm.checkIn && bookingForm.checkOut) {
          const normalizedCheckIn = normalizeDate(bookingForm.checkIn);
          const normalizedCheckOut = normalizeDate(bookingForm.checkOut);
          const nights = Math.ceil((normalizedCheckOut - normalizedCheckIn) / (1000 * 60 * 60 * 24));
          const basePrice = unit?.bookingProperty?.basePrice || 0;
          const calculatedTotal = basePrice * nights;

          setBookingForm(prev => ({
            ...prev,
            bookingPrice: {
              basePrice: basePrice,
              totalAmount: calculatedTotal,
              subTotal: calculatedTotal,
              nights: nights,
              currency: unit?.bookingProperty?.currencyId
            }
          }));
        }
      } finally {
        setBookingForm(prev => ({ ...prev, loading: false }));
      }
    };

    if (bookingForm.checkIn && bookingForm.checkOut && unit?.bookingProperty) {
      calculatePrice();
    }
  }, [bookingForm.checkIn, bookingForm.checkOut, unit?.bookingProperty, isManagedByLandlord]);

  const handleInputChange = (field, value) => {
    setBookingForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGuestChange = (type, value) => {
    setBookingForm(prev => ({
      ...prev,
      guests: {
        ...prev.guests,
        [type]: value
      }
    }));
  };

  const handleGuestInfoChange = (field, value) => {
    setGuestInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateGuestInfo = () => {
    const requiredFields = ['name', 'email', 'phone'];
    const missingFields = requiredFields.filter(field => !guestInfo[field]);

    if (missingFields.length > 0) {
      toastify(`Missing guest information: ${missingFields.join(', ')}`, "error");
      return false;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestInfo.email)) {
      toastify("Please enter a valid email address", "error");
      return false;
    }

    // Simple phone validation
    const phoneRegex = /^\d{7,15}$/;
    if (!phoneRegex.test(guestInfo.phone.replace(/\D/g, ''))) {
      toastify("Please enter a valid phone number", "error");
      return false;
    }

    if (!selectedStaffId) {
      toastify("Please select which staff member is making this booking", "error");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    // Validate dates
    if (!bookingForm.checkIn || !bookingForm.checkOut) {
      toastify("Please select check-in and check-out dates", "error");
      return;
    }

    const normalizedCheckIn = normalizeDate(bookingForm.checkIn);
    const normalizedCheckOut = normalizeDate(bookingForm.checkOut);

    console.log("Booking with normalized dates:");
    console.log("Check-in:", normalizedCheckIn.toString(), "Month:", normalizedCheckIn.getMonth());
    console.log("Check-out:", normalizedCheckOut.toString(), "Month:", normalizedCheckOut.getMonth());

    // Validate dates order
    if (normalizedCheckIn >= normalizedCheckOut) {
      toastify("Check-out date must be after check-in date", "error");
      return;
    }

    // Validate guest information
    if (!validateGuestInfo()) {
      return;
    }

    // Calculate nights
    const nights = Math.ceil((normalizedCheckOut - normalizedCheckIn) / (1000 * 60 * 60 * 24));

    // Check minimum/maximum stay for property manager apartments
      if (!isManagedByLandlord) {
        if (unit?.bookingProperty?.minimumStay && nights < unit.bookingProperty.minimumStay) {
          toastify(`Minimum stay for this property is ${unit.bookingProperty.minimumStay} nights`, "error");
          return;
        }

      if (unit?.bookingProperty?.maximumStay && nights > unit.bookingProperty.maximumStay) {
        toastify(`Maximum stay for this property is ${unit.bookingProperty.maximumStay} nights`, "error");
        return;
      }

      // Validate currency for property manager apartments
      if (!selectedCurrency?._id) {
        toastify("Missing currency information", "error");
        return;
      }
    }

    setSubmitting(true);

    try {
      const facilityId = await getItem("selectedFacilityId");
      const bookingPropertyId = unit?.bookingProperty?.id || unit?.bookingProperty?._id;

      if (!bookingPropertyId) {
        toastify("Missing booking property ID", "error");
        setSubmitting(false);
        return;
      }

      if (!unit?.unit?.id) {
        toastify("Missing unit ID", "error");
        setSubmitting(false);
        return;
      }

      // Base payload for both management types
      const selectedStaff = staffList.find((s) => s._id === selectedStaffId);

      const payload = {
        bookingPropertyId: bookingPropertyId,
        unitId: unit.unit.id,
        guestInfo: {
          name: guestInfo.name,
          email: guestInfo.email,
          phone: guestInfo.phone,
          idNumber: guestInfo.idNumber || ""
        },
        checkIn: normalizedCheckIn.toISOString(),
        checkOut: normalizedCheckOut.toISOString(),
        guests: {
          adults: bookingForm.guests.adults,
          children: bookingForm.guests.children || 0
        },
        specialRequests: bookingForm.specialRequests || "",
        servedBy: selectedStaffId,
        servedByName: selectedStaff?.fullName || ""
      };

      // Add financial details only for property manager managed apartments
      if (!isManagedByLandlord) {
        if (bookingForm.paymentMethod === 'Cash' && isCashAmountRequired()) {
          if (!cashPayment.amount || cashPayment.amount <= 0) {
            toastify("Please enter a valid cash amount", "error");
            setSubmitting(false);
            return;
          }
        }

        const totalAmount = bookingForm.bookingPrice?.totalAmount ||
          ((unit?.bookingProperty?.basePrice || 0) * Math.max(1, nights));

        const { amount: commission } = resolveCommission(unit.bookingProperty, Math.max(1, nights), totalAmount);
        const landlordAmount = totalAmount - commission;

        payload.basePrice = bookingForm.bookingPrice?.basePrice || unit.bookingProperty.basePrice || 0;
        payload.totalAmount = totalAmount;
        payload.commission = commission;
        payload.landlordAmount = landlordAmount;
        payload.currencyId = selectedCurrency._id;
        payload.paymentMethod = bookingForm.paymentMethod;
        payload.paymentTiming = bookingForm.paymentTiming;
        if (bookingForm.paymentTiming === 'Split') {
          if (!bookingForm.splitPaymentPercentage || bookingForm.splitPaymentPercentage <= 0 || bookingForm.splitPaymentPercentage > 100) {
            toastify("Please enter a valid split payment percentage (1-100)", "error");
            setSubmitting(false);
            return;
          }
          payload.splitPaymentPercentage = bookingForm.splitPaymentPercentage;
        }

        // Add facility payment details ID if Mobile Money is selected
        if (bookingForm.paymentMethod === 'Mobile Money') {
          if (!selectedPaybill) {
            toastify("Please select a paybill account for Mobile Money payment", "error");
            setSubmitting(false);
            return;
          }
          payload.facilityPaymentDetailsId = selectedPaybill;
        }
      }

      console.log("Creating reservation with payload:", JSON.stringify(payload, null, 2));

      const response = await makeRequest2(
        `${createBookingReservationURL}/${facilityId}`,
        "POST",
        payload
      );

      console.log("Booking API response:", response);

      if (response.success) {
        const bookingData = response.data?.data || response.data;
        const message = isManagedByLandlord
          ? "Booking created successfully (Landlord Managed)"
          : "Booking created successfully";

        toastify(message, "success");

        // Reset form
        setBookingForm({
          checkIn: null,
          checkOut: null,
          guests: {
            adults: 1,
            children: 0
          },
          specialRequests: '',
          loading: false,
          bookingPrice: null,
          paymentMethod: 'Card',
          paymentTiming: 'Before'
        });
        setGuestInfo({
          name: "",
          email: "",
          phone: "",
          idNumber: ""
        });
        setSelectedStaffId(null);
        setCashPayment({
          amount: 0,
          receiptNumber: '',
          notes: ''
        });

        onHide();

        if (!isManagedByLandlord && bookingData?.invoiceId && bookingForm.paymentMethod === 'Cash' && cashPayment.amount > 0) {
          try {
            const facilityIdForPayment = await getItem("selectedFacilityId");
            const paymentPayload = {
              amount: cashPayment.amount,
              paymentMethod: 'Cash',
              receiptNumber: cashPayment.receiptNumber,
              notes: cashPayment.notes || `Booking ${bookingData?.bookingReservationId || bookingData?._id || ''}`.trim(),
              paymentDate: new Date().toISOString()
            };

            const paymentResponse = await makeRequest2(
              `${UpdateBookingInvoicePaymentURL}/${facilityIdForPayment}/${bookingData.invoiceId}`,
              "PATCH",
              paymentPayload
            );

            if (paymentResponse.success) {
              toastify("Cash payment recorded. Pending finance approval.", "success");
            }
          } catch (paymentError) {
            console.error("Cash payment recording failed:", paymentError);
            toastify("Booking created, but cash payment could not be recorded.", "warning");
          }
        }

        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess(bookingData);
        }

        const statusForTrend = normalizeReservationStatus(
          bookingData?.status || (bookingForm.paymentTiming === 'After' ? 'booked' : 'reserved')
        );
        reservationTrendService.updateTrendData(new Date(), statusForTrend);
      } else {
        // Check if response contains 201 Created status
        if (response.error && response.error.includes('201')) {
          const message = isManagedByLandlord
            ? "Booking created successfully (Landlord Managed)"
            : "Booking created successfully";

          toastify(message, "success");

          // Reset and close
          setBookingForm({
            checkIn: null,
            checkOut: null,
            guests: {
              adults: 1,
              children: 0
            },
            specialRequests: '',
            loading: false,
            bookingPrice: null,
            paymentMethod: 'Card',
            paymentTiming: 'Before'
          });
          setGuestInfo({
            name: "",
            email: "",
            phone: "",
            idNumber: ""
          });
          setCashPayment({
            amount: 0,
            receiptNumber: '',
            notes: ''
          });

          onHide();

          if (onSuccess && typeof onSuccess === 'function') {
            onSuccess({ message: "Booking created successfully" });
          }

          const statusForTrend = bookingForm.paymentTiming === 'After' ? 'booked' : 'reserved';
          reservationTrendService.updateTrendData(new Date(), statusForTrend);
        } else {
          console.error("Error in booking process:", response.error);
          toastify(response.error || "Failed to create booking", "error");
        }
      }
    } catch (error) {
      console.error("Unexpected error in booking process:", error);
      toastify("An unexpected error occurred while creating the booking", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Format currency (property manager only)
  const formatCurrency = (amount) => {
    if (isManagedByLandlord || !selectedCurrency) return 'N/A';

    const code = selectedCurrency.currencyShortCode || '$';
    if (amount === undefined || amount === null) {
      return `${code}0.00`;
    }
    return `${code}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Handle error case
  if (!unit || !unit.bookingProperty) {
    return (
      <Dialog
        header="Booking Error"
        visible={visible}
        onHide={onHide}
        style={{ width: '400px' }}
      >
        <div className="p-3 text-center">
          <i className="ti ti-alert-triangle text-warning" style={{ fontSize: '3rem' }}></i>
          <h5 className="mt-3">Unable to Load Apartment Details</h5>
          <p>There was a problem loading the Apartment information. Please try again.</p>
          <Button label="Close" icon="ti ti-x" className="p-button-primary mt-3" onClick={onHide} />
        </div>
      </Dialog>
    );
  }

  // Calculate nights for display
  const nights = bookingForm.checkIn && bookingForm.checkOut
    ? Math.ceil((bookingForm.checkOut - bookingForm.checkIn) / (1000 * 60 * 60 * 24))
    : 0;

  // Calculate total for property manager apartments
  const totalAmount = !isManagedByLandlord ? (
    bookingForm.bookingPrice?.totalAmount ||
    ((unit?.bookingProperty?.basePrice || 0) * Math.max(1, nights))
  ) : 0;

  return (
    <Dialog
      header={`Book ${unit?.unit?.name || 'Apartment'} ${isManagedByLandlord ? '(Landlord Managed)' : ''}`}
      visible={visible}
      style={{ width: '550px' }}
      onHide={onHide}
      footer={
        <div className="d-flex justify-content-end gap-2">
          <Button
            label="Cancel"
            icon="ti ti-x"
            className="p-button-text"
            onClick={onHide}
            disabled={submitting}
          />
          <Button
            label={isManagedByLandlord ? "Reserve Apartment" : "Book Now"}
            icon="ti ti-calendar-plus"
            className="p-button-primary"
            onClick={handleSubmit}
            loading={submitting || bookingForm.loading}
          />
        </div>
      }
    >
      <div className="p-fluid">
        {/* Management Type Indicator */}
        {isManagedByLandlord && (
          <div className="alert alert-info mb-3">
            <i className="ti ti-info-circle me-2"></i>
            <strong>Landlord Managed Apartment:</strong> Basic reservation details only.
          </div>
        )}

        {/* Guest Information Section */}
        <div className="row mb-3">
          <div className="col-12">
            <h6 className="mb-2">Guest Information</h6>
          </div>
          <div className="col-md-6 mb-2">
            <label className="form-label">Full Name <span className="text-danger">*</span></label>
            <InputText
              value={guestInfo.name}
              onChange={(e) => handleGuestInfoChange('name', e.target.value)}
              placeholder="Enter guest name"
              className="w-100"
            />
          </div>
          <div className="col-md-6 mb-2">
            <label className="form-label">Email <span className="text-danger">*</span></label>
            <InputText
              value={guestInfo.email}
              onChange={(e) => handleGuestInfoChange('email', e.target.value)}
              placeholder="Email address"
              className="w-100"
            />
          </div>
          <div className="col-md-6 mb-2">
            <label className="form-label">Phone <span className="text-danger">*</span></label>
            <PhoneInput
              country={'ke'}
              value={guestInfo.phone}
              onChange={(value) => handleGuestInfoChange('phone', value ? '+' + value : '')}
              enableSearch={true}
              countryCodeEditable={false}
              inputProps={{ required: true, name: 'phone' }}
              inputStyle={{ width: '100%', height: '38px', fontSize: '14px' }}
              containerStyle={{ width: '100%' }}
              placeholder="Phone number"
            />
            <small className="text-muted">Pick a country to capture the correct dialling code — WhatsApp will deliver to international numbers as well.</small>
          </div>
          <div className="col-md-6 mb-2">
            <label className="form-label">ID Number</label>
            <InputText
              value={guestInfo.idNumber}
              onChange={(e) => handleGuestInfoChange('idNumber', e.target.value)}
              placeholder="Passport/ID number"
              className="w-100"
            />
          </div>
          <div className="col-md-6 mb-2">
            <label className="form-label">Staff <span className="text-danger">*</span></label>
            <Dropdown
              value={selectedStaffId}
              options={staffList.map((s) => ({ label: s.fullName, value: s._id }))}
              onChange={(e) => setSelectedStaffId(e.value)}
              placeholder="Select which staff member is making this booking"
              className="w-100"
              filter
            />
            <small className="text-muted">Shown on the invoice as "You were served by ..."</small>
          </div>
        </div>

        {/* Booking Dates */}
        <div className="row">
          <div className="col-12 mb-3">
            <label className="form-label">Select Dates <span className="text-danger">*</span></label>
            <BookingCalendar
              bookingPropertyId={unit?.bookingProperty?.id || unit?.bookingProperty?._id}
              selectedDates={[bookingForm.checkIn, bookingForm.checkOut].filter(Boolean)}
              onDateChange={(e) => {
                console.log("Booking date selection changed:", e);

                if (!e.value || e.value.length === 0) {
                  setBookingForm(prev => ({
                    ...prev,
                    checkIn: null,
                    checkOut: null
                  }));
                  return;
                }

                if (Array.isArray(e.value)) {
                  if (e.value.length === 1) {
                    const normalizedDate = normalizeDate(e.value[0]);
                    setBookingForm(prev => ({
                      ...prev,
                      checkIn: normalizedDate,
                      checkOut: null
                    }));
                  } else if (e.value.length === 2) {
                    const normalizedCheckIn = normalizeDate(e.value[0]);
                    const normalizedCheckOut = normalizeDate(e.value[1]);

                    setBookingForm(prev => ({
                      ...prev,
                      checkIn: normalizedCheckIn,
                      checkOut: normalizedCheckOut
                    }));
                  }
                }
              }}
              minDate={new Date()}
              showBlockedReason={true}
            />
            <small className="text-muted d-block mt-2">Blocked dates cannot be selected</small>
          </div>

          {/* Guests */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Adults</label>
            <InputNumber
              value={bookingForm.guests.adults}
              onChange={(e) => handleGuestChange('adults', e.value)}
              className="w-100"
              min={1}
              max={10}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Children</label>
            <InputNumber
              value={bookingForm.guests.children}
              onChange={(e) => handleGuestChange('children', e.value)}
              className="w-100"
              min={0}
              max={10}
            />
          </div>

          {/* Payment Details - Only for Property Manager */}
          {!isManagedByLandlord && (
            <>
              <div className="col-md-6 mb-3">
                <label className="form-label">Payment Method <span className="text-danger">*</span></label>
                <Dropdown
                  value={bookingForm.paymentMethod}
                  options={paymentMethods}
                  onChange={(e) => handleInputChange('paymentMethod', e.value)}
                  optionLabel="name"
                  optionValue="value"
                  placeholder="Select payment method"
                  className="w-100"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Payment Timing <span className="text-danger">*</span></label>
                <Dropdown
                  value={bookingForm.paymentTiming}
                  options={paymentTimings}
                  onChange={(e) => handleInputChange('paymentTiming', e.value)}
                  optionLabel="name"
                  optionValue="value"
                  placeholder="Select payment timing"
                  className="w-100"
                />
                {bookingForm.paymentTiming === 'Split' && (
                  <>
                    <InputNumber
                      value={bookingForm.splitPaymentPercentage}
                      onValueChange={(e) => handleInputChange('splitPaymentPercentage', e.value)}
                      className="w-100 mt-2"
                      suffix="%"
                      min={1}
                      max={100}
                      placeholder="e.g. 50"
                    />
                    <small className="text-muted d-block mt-1">
                      Guest must pay at least this percentage of the total before check-in is allowed.
                    </small>
                  </>
                )}
                {bookingForm.paymentTiming === 'PerNight' && (
                  <small className="text-muted d-block mt-1">
                    Guest pays after each night. Reminders are sent before the facility's day-end cutoff.
                  </small>
                )}
              </div>

              {/* Paybill Dropdown - Show only when Mobile Money is selected */}
              {bookingForm.paymentMethod === 'Mobile Money' && (
                <div className="col-12 mb-3">
                  <label className="form-label">
                    Select Paybill Account <span className="text-danger">*</span>
                  </label>
                  <Dropdown
                    value={selectedPaybill}
                    options={Array.isArray(paybills) ? paybills : []}
                    onChange={(e) => setSelectedPaybill(e.value)}
                    optionLabel="label"
                    optionValue="_id"
                    placeholder="Select paybill account"
                    className="w-100"
                  />
                  {paybills.length === 0 && (
                    <small className="text-danger d-block mt-1">
                      No paybill accounts configured. Please contact your administrator.
                    </small>
                  )}
                  {selectedPaybill && (
                    <small className="text-muted d-block mt-1">
                      Payment will be processed through this paybill account
                    </small>
                  )}
                  <div className="mt-2">
                    <Button
                      label="Pay with M-Pesa (Coming Soon)"
                      icon="ti ti-device-mobile"
                      className="p-button-secondary"
                      disabled
                    />
                  </div>
                </div>
              )}

              {bookingForm.paymentMethod === 'Cash' && (
                <>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Cash Amount {isCashAmountRequired() && <span className="text-danger">*</span>}
                    </label>
                    <InputNumber
                      value={cashPayment.amount}
                      onValueChange={(e) => setCashPayment((prev) => ({
                        ...prev,
                        amount: e.value || 0
                      }))}
                      className="w-100"
                      min={0}
                      mode="decimal"
                      placeholder="Enter cash amount"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Receipt Number</label>
                    <InputText
                      value={cashPayment.receiptNumber}
                      readOnly
                      className="w-100"
                      placeholder="Auto-generated"
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label">Payment Notes (optional)</label>
                    <InputText
                      value={cashPayment.notes}
                      onChange={(e) => setCashPayment((prev) => ({
                        ...prev,
                        notes: e.target.value
                      }))}
                      className="w-100"
                      placeholder="Add payment notes"
                    />
                    <small className="text-muted d-block mt-1">
                      Cash payments are sent to Finance for approval and will update the invoice status once approved.
                    </small>
                  </div>
                </>
              )}
            </>
          )}

          <div className="col-12 mb-3">
            <label className="form-label">Special Requests</label>
            <InputText
              value={bookingForm.specialRequests}
              onChange={(e) => handleInputChange('specialRequests', e.target.value)}
              className="w-100"
              placeholder="Any special requests?"
            />
          </div>

          {/* Booking Summary - Only for Property Manager */}
          {!isManagedByLandlord && (
            <div className="col-12">
              <div className="booking-summary p-3 bg-light rounded">
                <h6 className="mb-3">Booking Summary</h6>
                <div className="d-flex justify-content-between mb-2">
                  <span>Apartment:</span>
                  <strong>{unit?.unit?.name || 'N/A'}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Apartment Type:</span>
                  <strong>{unit?.unit?.unitType || 'N/A'}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Base Price:</span>
                  <strong>
                    {unit?.bookingProperty?.basePrice !== undefined
                      ? formatCurrency(unit.bookingProperty.basePrice)
                      : 'N/A'}
                  </strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Currency:</span>
                  <strong>
                    {selectedCurrency
                      ? `${selectedCurrency.currencyName} (${selectedCurrency.currencyShortCode})`
                      : 'N/A'}
                  </strong>
                </div>

                {nights > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>Nights:</span>
                    <strong>{nights}</strong>
                  </div>
                )}

                <hr className="my-3" />
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Amount:</span>
                  <strong className="text-success">
                    {formatCurrency(totalAmount)}
                  </strong>
                </div>
              </div>
            </div>
          )}

          {/* Simple Summary for Landlord Managed */}
          {isManagedByLandlord && (
            <div className="col-12">
              <div className="booking-summary p-3 bg-light rounded">
                <h6 className="mb-3">Reservation Summary</h6>
                <div className="d-flex justify-content-between mb-2">
                  <span>Apartment:</span>
                  <strong>{unit?.unit?.name || 'N/A'}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Apartment Type:</span>
                  <strong>{unit?.unit?.unitType || 'N/A'}</strong>
                </div>
                {nights > 0 && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>Duration:</span>
                    <strong>{nights} {nights === 1 ? 'night' : 'nights'}</strong>
                  </div>
                )}
                <div className="d-flex justify-content-between mb-2">
                  <span>Total Guests:</span>
                  <strong>{bookingForm.guests.adults + (bookingForm.guests.children || 0)}</strong>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default BookingModal;
